import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { useSession } from '../contexts/SessionContext';
import { useCart } from '../contexts/CartContext';
import PageLayout from './common/PageLayout';
import {
  Container,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Box,
  IconButton,
  Divider,
  Paper,
  Stack,
  Chip,
  Badge,
} from '@mui/material';
import PartialLinePayField from './common/PartialLinePayField';
import { 
  Add as AddIcon, 
  Remove as RemoveIcon, 
  ArrowBack as ArrowBackIcon,
  ShoppingCart as ShoppingCartIcon,
  Home as HomeIcon,
  DeleteForever as DeleteForeverIcon
} from '@mui/icons-material';
import {
  getLineChargeTotal,
  getLineListTotal,
  isPartialPaymentEligible,
  isPartialLineAmountUnset,
  getPartialMinPay
} from '../utils/cartPricing';

const CartPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppContext();
  const { session, resetTimer } = useSession();
  const { cart, updateQuantity, updateItemPayLineTotal, clearItemPayLineTotal, removeFromCart, clearCart } = useCart();

  // Determine origin for navigation context
  const origin = (location?.state && location.state.from) || session?.lastOrigin || 'products';
  const originPath = origin === 'kotilinga' ? '/kotilinga' : '/shop';

  // Do not auto-redirect when cart is empty; show empty state instead
  useEffect(() => {
    console.log('CartPage - cart changed:', cart);
  }, [cart]);
  
  // Reset inactivity timer on user interaction
  useEffect(() => {
    resetTimer();
  }, [resetTimer]);
  
  // Keep user on the cart page even when empty (no redirect)
  useEffect(() => {
    // intentionally left blank to avoid redirecting away when empty
  }, [cart.length]);

  // Update URL state when cart changes
  useEffect(() => {
    if (cart.length > 0) {
      window.history.replaceState({}, '', window.location.href);
      window.history.replaceState({ cart }, '');
    }
  }, [cart]);

  const handleQuantityChange = (itemId, change) => {
    const item = cart.find(item => item.id === itemId || item.cart_item_id === itemId);
    if (!item) {
      console.error('Item not found in cart:', itemId);
      return;
    }
    
    const currentQuantity = typeof item.quantity === 'number' ? item.quantity : 1;
    const newQuantity = Math.max(1, currentQuantity + change);
    // Always keep at least quantity 1; do not remove on decrement to 1
    const idToUpdate = item.id || item.cart_item_id;
    updateQuantity(idToUpdate, newQuantity);
    resetTimer();
  };

  const handleRemoveItem = (itemId) => {
    removeFromCart(itemId);
    resetTimer();
  };

  // Handle checkout - redirect to payment if user is logged in or userSelectedFlag is true, otherwise go to checkout
  const handleCheckout = () => {
    console.log('handleCheckout - session:', session);
    console.log('handleCheckout - cart:', cart);
    
    if (!cart || cart.length === 0) {
      console.log('Cart is empty, cannot proceed to checkout');
      return;
    }

    if (cart.some((i) => isPartialLineAmountUnset(i))) {
      alert('Enter an amount for every line where you used clear (X). Use the field to type the initial payment, then continue to payment.');
      return;
    }
    
    const total = cart.reduce((sum, item) => sum + getLineChargeTotal(item), 0);
    
    if (session?.user?.id || session?.userSelectedFlag) {
      console.log('User is logged in or userSelectedFlag is true, going to payment');
      // If user is already logged in or userSelectedFlag is true, go to payment
      // Get guest user data from session or use empty object
      const guestUser = session?.user || {};
      
      const state = { 
        cart: [...cart],
        devotee: guestUser,
        total,
        fromCart: true,
        isGuestCheckout: !session?.user?.id // Mark as guest checkout if no user ID
      };
      
      console.log('Navigating to payment with state:', state);
      navigate('/payment', { 
        state: { ...state, from: origin },
        replace: false // Changed to false to allow back navigation
      });
    } else {
      console.log('User is not logged in, going to checkout');
      // Otherwise, go to checkout to collect user information
      navigate('/checkout', { 
        state: { 
          cart: [...cart],
          total,
          from: origin
        },
        replace: false // Changed to false to allow back navigation
      });
    }
  };

  const calculateTotal = () => {
    if (!Array.isArray(cart) || cart.length === 0) return 0;
    return cart.reduce((total, item) => total + getLineChargeTotal(item), 0);
  };

  // Format item name for cart. Preserve a spaced hyphen between product and variation (Product - Variation)
  const formatVariationName = (name) => {
    try {
      const str = String(name || '');
      const capWords = (s) => s
        .split(' ')
        .filter(Boolean)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');

      if (str.includes(' - ')) {
        const [product, variation] = str.split(' - ');
        return `${capWords(product)} - ${capWords(variation)}`;
      }

      // Fallback: normalize spaces and capitalize words (legacy names without explicit separator)
      const withSpaces = str.replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
      return capWords(withSpaces);
    } catch (error) {
      console.error('Error formatting name:', error);
      return 'Item';
    }
  };

  // Calculate total number of items in cart with safe defaults
  const totalItems = Array.isArray(cart) 
    ? cart.reduce((total, item) => {
        const quantity = Number(item?.quantity) || 1;
        return total + (isNaN(quantity) ? 0 : quantity);
      }, 0)
    : 0;

  return (
    <PageLayout contentMaxWidth="xl">
      <Box sx={{ py: 0 }}>
      {/* Top action buttons outside the box */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flex: '0 0 auto' }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(originPath)} 
            size="large"
            sx={{
              minWidth: 200,
              px: 3,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              backgroundColor: '#f3e5f5',
              borderColor: '#d1c4e9',
              color: '#4a148c',
              borderWidth: 1.5,
              borderRadius: 1.5,
              '&:hover': {
                backgroundColor: '#e1bee7',
                borderColor: '#b39ddb'
              }
            }}
          >
            Back
          </Button>
        </Box>
        <Box sx={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            startIcon={<HomeIcon />} 
            onClick={() => navigate('/home')} 
            size="large"
            sx={{
              minWidth: 200,
              px: 3,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              backgroundColor: '#f3e5f5',
              borderColor: '#d1c4e9',
              color: '#4a148c',
              borderWidth: 1.5,
              borderRadius: 1.5,
              '&:hover': {
                backgroundColor: '#e1bee7',
                borderColor: '#b39ddb'
              }
            }}
          >
            Home
          </Button>
        </Box>
        <Box sx={{ flex: '0 0 auto' }}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              clearCart();
              resetTimer();
            }}
            startIcon={<ShoppingCartIcon />}
            size="large"
            sx={{
              minWidth: 200,
              px: 3,
              py: 1.2,
              fontSize: '1rem',
              fontWeight: 600,
              backgroundColor: '#ffebee',
              borderColor: '#ffcdd2',
              color: 'error.main',
              borderWidth: 1.5,
              borderRadius: 1.5,
              '&:hover': {
                backgroundColor: '#ffcdd2',
                borderColor: '#ef9a9a'
              }
            }}
          >
            Clear Cart ({totalItems})
          </Button>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Your Cart
        </Typography>
        
        {cart.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
              Your cart is empty
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate(originPath)}
              sx={{
                minWidth: 280,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              Browse Donations, Events & Services
            </Button>
          </Box>
        ) : (
          <>
            <List>
              {Array.isArray(cart) && cart.map((item) => {
                // Ensure we have valid item data
                if (!item || typeof item !== 'object') return null;
                
                const itemIdRaw = item.id || item.cart_item_id || Math.random().toString(36).substr(2, 9);
                const itemId = String(itemIdRaw);
                const itemName = String(item.name || 'Unnamed Item');
                const itemPrice = parseFloat(item.price) || 0;
                const itemQuantity = parseInt(item.quantity, 10) || 1;
                const lineList = getLineListTotal(item);
                const lineCharge = getLineChargeTotal(item);
                const partialEligible = isPartialPaymentEligible(item);
                const partialMin = getPartialMinPay(item);
                const showPartial = partialEligible && partialMin != null;
                const partialUnset = isPartialLineAmountUnset(item);
                // Check for image in different possible locations
                const itemImage = item.image || // Check direct image property first
                                item.image?.src || 
                                item.images?.[0]?.src || 
                                item.product_image || 
                                (item.variation && item.variation.image && item.variation.image.src) || '';
                
                return (
                <React.Fragment key={itemId}>
                  <ListItem alignItems="flex-start" data-item-id={itemId}>
                    <ListItemAvatar>
                      <Avatar 
                        src={itemImage} 
                        alt={itemName}
                        variant="rounded"
                        sx={{ 
                          width: 60, 
                          height: 60, 
                          mr: 2, 
                          borderRadius: 1,
                          bgcolor: 'background.paper'
                        }}
                      >
                        {!itemImage && <ShoppingCartIcon />}
                      </Avatar>
                    </ListItemAvatar>
                    <Box sx={{ flex: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" component="div" noWrap>
                          {formatVariationName(itemName)}
                        </Typography>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h6" color="primary">
                            {partialUnset ? '—' : (
                              <>
                                ${lineCharge.toFixed(2)}
                                {lineCharge + 0.01 < lineList && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
                                    of ${lineList.toFixed(2)} full
                                  </Typography>
                                )}
                              </>
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      {showPartial && (
                        <Box sx={{ maxWidth: 400 }}>
                          <PartialLinePayField
                            itemId={itemIdRaw}
                            lineList={lineList}
                            minPay={partialMin}
                            value={item.pay_line_total}
                            payLineCleared={!!item.pay_line_cleared}
                            onCommit={updateItemPayLineTotal}
                            onClear={clearItemPayLineTotal}
                          />
                        </Box>
                      )}
                      <Box display="flex" alignItems="center" mt={1}>
                        <IconButton 
                          size="medium"
                          onClick={() => handleQuantityChange(itemId, -1)}
                          disabled={itemQuantity <= 1}
                          aria-label="Decrease quantity"
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: '#f3e5f5',
                            border: '1px solid',
                            borderColor: '#d1c4e9',
                            color: '#4a148c',
                            '&:hover': { bgcolor: '#e1bee7', borderColor: '#b39ddb' },
                            '&.Mui-disabled': { opacity: 0.5 }
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Chip 
                          label={itemQuantity} 
                          color="primary" 
                          variant="outlined" 
                          sx={{ mx: 1.5, minWidth: 44, height: 36, fontWeight: 600 }} 
                        />
                        <IconButton 
                          size="medium"
                          onClick={() => handleQuantityChange(itemId, 1)}
                          aria-label="Increase quantity"
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: '#f3e5f5',
                            border: '1px solid',
                            borderColor: '#d1c4e9',
                            color: '#4a148c',
                            '&:hover': { bgcolor: '#e1bee7', borderColor: '#b39ddb' }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          size="medium"
                          onClick={() => handleRemoveItem(itemId)}
                          aria-label="Remove item"
                          sx={{
                            ml: 1.5,
                            width: 40,
                            height: 40,
                            bgcolor: '#ffebee',
                            border: '1px solid',
                            borderColor: '#ffcdd2',
                            color: 'error.main',
                            '&:hover': { bgcolor: '#ffcdd2', borderColor: '#ef9a9a' }
                          }}
                        >
                          <DeleteForeverIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItem>
                  <Divider component="li" sx={{ my: 1 }} />
                </React.Fragment>
              );
            })}
            </List>
            
            <Box sx={{ mt: 3, textAlign: 'right' }}>
              <Typography variant="h6" gutterBottom>
                Total: ${calculateTotal().toFixed(2)}
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCheckout}
                disabled={cart.length === 0}
                startIcon={<ShoppingCartIcon />}
                size="large"
                fullWidth
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  borderRadius: '50px',
                  '&:hover': {
                    transform: 'scale(1.01)',
                    boxShadow: 6
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                {`Continue to Checkout (${totalItems} ${totalItems === 1 ? 'Item' : 'Items'})`}
              </Button>
            </Box>
          </>
        )}
      </Paper>
      </Box>
    </PageLayout>
  );
};

export default CartPage;
